import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Autocomplete, Box, styled, Typography } from "@mui/material";
import { useRouteMatch } from "react-router-dom";
import { Form, Field, FormikProps } from "formik";
import { Dialog, Paper, TextField, Button } from "@mui/material";

import NewPrice from "../NewPrice";
import Properties from "../Properties";
import downloadCsv from "../../../../utils/downloadCsv";
import QuantityOptions from "../QuantityOptions";
import DurationControls from "../DurationControls";
import BatchCreateNTimes from "../BatchCreateNTimes";
import CreateButtonGroup from "../CreateButtonGroup";
import { discountCodesActions } from "../../../../reducers/discountCodes";
import { FormInput, FormSelect } from "../../index";
import InputWithRandomGeneratorString from "../../InputWithRandomGeneratorString";

import { ICode } from "../types";
import { APIDiscounts } from "../../../../api";
import { get_plan_options } from "../PlansOptions";
import { useAdminProviderDataContext } from "../../../../context";
import { DiscountCodeState, IUserEmailWithId } from "../../../../api/discounts/index.types";
import { MAX_LENGTH_DISCOUNT_CODE_DESCRIPTION } from "../../../../constants";

interface IProps {
  formikProps: FormikProps<ICode>;
  isSavingCode: boolean;
  isEditingDiscountCode?: boolean;
  id?: number;
  selectedDiscountCode?: DiscountCodeState | undefined;
  prefilledEmail?: string;
  isOpenDownloadCSVDialog: boolean;
  handleCloseDialog: () => void;
  handleCloseDownloadCsvDialog: (cb: () => void) => void;
}

const Container = styled(Box)({
  minWidth: 515,
  maxWidth: 800,
  padding: 30,
  background: "rgb(247, 247, 247)",
});

const DiscountForm: React.FC<IProps> = ({
  formikProps,
  isSavingCode,
  isEditingDiscountCode,
  id,
  selectedDiscountCode,
  prefilledEmail,
  isOpenDownloadCSVDialog,
  handleCloseDialog,
  handleCloseDownloadCsvDialog,
}) => {
  const dispatch = useDispatch();
  const matchCustomersPage = useRouteMatch("/customers");
  const { plans } = useSelector((state) => state.plans.data);
  const { user } = useAdminProviderDataContext();
  const discountCodes = useSelector((state) => state.discountCodes);

  const [linkUserQueryEmail, setLinkUserQueryEmail] = useState<string>("");
  const [linkUserList, setLinkUserList] = useState<IUserEmailWithId[]>([]);

  const autoCompleteDefaultIdValue = id || selectedDiscountCode?.accounts_id;
  const isUsePrefilledAutoCompleteDefaultValue =
    prefilledEmail || selectedDiscountCode?.accounts_id;
  const autoCompleteDefaultEmailValue = prefilledEmail || selectedDiscountCode?.account.email;

  const handleLinkUserSearch = (target: string) => {
    if (target.length < 3) return; // don't query 1-2 letters
    setLinkUserQueryEmail(target);
  };

  const handleDownloadCsv = () => {
    const HEADER = ["Code", "Description"];
    const rows: string[][] = [HEADER];
    discountCodes.data.discount_codes.forEach((discountCode) => {
      const { desc } = discountCode;
      rows.push([discountCode.code, desc.replaceAll(",", " ")]);
    });
    downloadCsv(rows, "Discounts for current users");

    dispatch(
      discountCodesActions.fetchSuccess({
        discount_codes: [],
        count: 0,
        usedDiscountCodesWithFullDiscount: [],
      })
    );
  };

  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (linkUserQueryEmail.length < 3) return; // don't query 1-2 letters
      try {
        const response = await APIDiscounts.getEmailsListRequest(
          { email: linkUserQueryEmail },
          undefined
        );
        setLinkUserList(response.data);
      } catch (error) {
        console.error("Error getting emails", error);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [linkUserQueryEmail]);

  const { submitForm, values, setFieldValue, isValid, dirty } = formikProps;
  return (
    <Form>
      <Container>
        <BatchCreateNTimes
          isSavingCode={isSavingCode}
          submitForm={submitForm}
          code={values}
          setFieldValue={setFieldValue}
        />

        <div>
          <Typography fontSize={24} fontWeight={700}>
            {isEditingDiscountCode ? "Update code" : "Create code"}
          </Typography>
        </div>
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Box flexDirection="column" display="flex" mr="30px" minWidth="320px">
            <InputWithRandomGeneratorString
              label={matchCustomersPage ? "Code template" : "Code"}
              name="code"
              data-test="discount-code-input"
              setFieldValue={setFieldValue}
            />

            {!matchCustomersPage && (
              // TODO make reusable search auto autocomplete component
              <Autocomplete
                {...(autoCompleteDefaultIdValue &&
                  isUsePrefilledAutoCompleteDefaultValue && {
                    defaultValue: {
                      id: autoCompleteDefaultIdValue || -1,
                      email: autoCompleteDefaultEmailValue,
                    },
                  })}
                disabled={!!(id && prefilledEmail)}
                options={linkUserList}
                // @ts-ignore
                getOptionLabel={(option) => option.email}
                onChange={(_, newValue) =>
                  setFieldValue("accounts_id", newValue ? newValue.id : null)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label="Link to user"
                    onChange={(e) => handleLinkUserSearch(e.target.value)}
                  />
                )}
              />
            )}

            <Box mt="10px" mb="20px">
              <Field
                name="plan"
                label="Plan"
                placeholder="Plan"
                component={FormSelect}
                options={get_plan_options(plans)}
                variant="standard"
                data-test="plan-select"
              />
            </Box>

            <Field
              name="amount"
              component={FormInput}
              data-test="amount-input"
              label={values.percent ? "Percent" : "Discount amount"}
              type="number"
              variant="standard"
              marginBottom="10px"
            />
            <TextField
              variant="standard"
              error={values.descr.length > MAX_LENGTH_DISCOUNT_CODE_DESCRIPTION}
              label={matchCustomersPage ? "Description template" : "Description"}
              value={values.descr}
              onChange={(e) => {
                setFieldValue("descr", e.target.value);
              }}
              helperText={`Please specify a description! ${
                MAX_LENGTH_DISCOUNT_CODE_DESCRIPTION - values.descr.length
              }/${MAX_LENGTH_DISCOUNT_CODE_DESCRIPTION}`}
            />

            <QuantityOptions />

            <DurationControls values={values} setFieldValue={setFieldValue} />
          </Box>

          <div>
            <Paper
              variant="outlined"
              sx={{
                margin: "10px 0",
                padding: "30px 40px",
                minWidth: 150,
              }}
            >
              <Properties
                values={values}
                isEditingDiscountCode={!!isEditingDiscountCode}
                setFieldValue={setFieldValue}
              />
            </Paper>
          </div>
        </Box>

        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <div />
          <Box textAlign="right">
            <NewPrice values={values} isNewPrice={!values.percent} />
            <br />

            <CreateButtonGroup
              isValid={isValid}
              dirty={dirty}
              code={values}
              isSavingCode={isSavingCode}
              isAvailableBatchCreating={
                isEditingDiscountCode ? false : user.allowed_features.batch_create_codes
              }
              isEditingDiscountCode={!!isEditingDiscountCode}
              setFieldValue={setFieldValue}
              data-test="create-button-group"
            />
          </Box>
        </Box>

        <Dialog open={isOpenDownloadCSVDialog} onClose={handleCloseDialog}>
          <Container display="flex" flexDirection="column" alignItems="center">
            <h3>Codes made successfully</h3>
            <Button onClick={() => handleCloseDownloadCsvDialog(handleDownloadCsv)}>
              Download codes
            </Button>
          </Container>
        </Dialog>
      </Container>
    </Form>
  );
};

export default DiscountForm;
